// Health: one card per line (heartbeat, queue, webhook), open alarms with what to do,
// recent error codes, repetition, and the webhook tail. Refreshes on SSE 'health'.

import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALARM_ACTIONS, errorCodeText, formatPhone } from '@bt/core';
import { api, money, type HealthEvent, type Line, type MeResponse } from '../../api';
import { Loading, ErrorBox, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ageSeconds, fmtAgeSeconds, fmtAgo, fmtEvery, fmtFull, fmtRelative, linePaused, pluralize } from '../../lib/format';

/** The phone's own event names, in words the office understands. */
function eventWords(type: string | null | undefined): string {
  switch (type) {
    case 'message.phone.received': return 'A customer texted us';
    case 'message.phone.sent': return 'The phone sent one';
    case 'message.phone.delivered': return 'Delivered to the customer';
    case 'message.send.failed': return 'The phone could not send one';
    case 'message.send.expired': return 'Gave up after the phone slept too long';
    case 'phone.heartbeat.online': return 'The phone checked in';
    case 'phone.heartbeat.offline': return 'The phone went quiet';
    case 'message.call.missed': return 'A missed call';
    default: return type ?? 'Something from the phone';
  }
}

/** What we did with it. */
function outcomeWords(outcome: string | null | undefined): string {
  switch (outcome) {
    case 'inbound_created': return 'saved to the inbox';
    case 'status_updated': return 'updated a text we sent';
    case 'ignored': return 'ignored on purpose';
    case 'duplicate': return 'already had it';
    case 'error': return 'could not process it';
    case 'processing': return 'working on it';
    default: return outcome ?? '';
  }
}

export function HealthPage({ me }: { me: MeResponse }) {
  const q = useQuery({ queryKey: ['health'], queryFn: api.health.get, refetchInterval: 60_000 });
  // phase 2: the AI month line comes from /api/ai/usage unless the health route already carries it
  const aiUsage = useQuery({ queryKey: ['ai-usage'], queryFn: api.ai.usage, retry: false, staleTime: 60_000, enabled: !q.data?.ai });
  const qc = useQueryClient();
  const toast = useToast();
  const ack = useMutation({
    mutationFn: (id: string) => api.health.ack(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['health'] });
      toast({ text: r.alarm?.resolved_at ? 'Acknowledged and cleared.' : 'Acknowledged. The red bar is off for this one; it stays here until it clears.', tone: 'ok' });
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });
  const resume = useMutation({
    mutationFn: (id: string) => api.lines.resume(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health'] });
      qc.invalidateQueries({ queryKey: ['lines'] });
      toast({ text: 'Line resumed.', tone: 'ok' });
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });

  if (q.isPending) return <Loading label="Checking the lines" />;
  if (q.isError)
    return (
      <div className="page">
        <ErrorBox error={q.error} retry={() => q.refetch()} />
      </div>
    );
  const h = q.data;
  const open = h.alarms.filter((a) => !a.resolved_at);
  const isAdmin = me.user.role === 'admin';
  const tz = me.workspace.timezone;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Health</h1>
        <div className="row">
          <span className="muted small">
            {h.counters ? `${h.counters.queued} waiting · ${h.counters.sent_24h} sent today · ${h.counters.failed_24h} failed · ${h.counters.inbound_24h} replies in` : ''}
          </span>
          <Link to="/health/self-test" className="btn">
            Run two-way test
          </Link>
        </div>
      </div>

      {(h.ai || aiUsage.data || h.counters?.staff_alerts_today != null) && (
        <div className="muted small" style={{ marginBottom: 12 }}>
          {h.ai
            ? `AI this month: $${money(h.ai.month_cost_usd).toFixed(2)} · ${pluralize(h.ai.suggestions_month, 'suggestion')}`
            : aiUsage.data
              ? aiUsage.data.configured
                ? `AI this month: $${money(aiUsage.data.month?.cost_usd).toFixed(2)} · ${pluralize(aiUsage.data.month?.suggestions ?? 0, 'suggestion')}`
                : 'AI: not set up'
              : ''}
          {h.counters?.staff_alerts_today != null && `${h.ai || aiUsage.data ? ' · ' : ''}staff alerts sent today: ${h.counters.staff_alerts_today}`}
        </div>
      )}

      {open.length > 0 && (
        <div className="stack" style={{ marginBottom: 16 }}>
          {open.map((a) => (
            <AlarmCard key={a.id} a={a} lines={h.lines} tz={tz} onAck={() => ack.mutate(a.id)} acking={ack.isPending} />
          ))}
        </div>
      )}

      {h.lines.length === 0 && <Empty title="No texting line yet" hint="An admin adds one under Settings › Lines." />}
      {h.lines.map((line) => (
        <LineCard key={line.id} line={line} tz={tz} isAdmin={isAdmin} onResume={() => resume.mutate(line.id)} resuming={resume.isPending} />
      ))}

      <div className="card">
        <h2>Recent errors</h2>
        {h.recent_errors.length === 0 ? (
          <div className="muted">No errors recently.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Code</th>
                  <th>Meaning</th>
                  <th>To</th>
                  <th>Text</th>
                </tr>
              </thead>
              <tbody>
                {h.recent_errors.map((m) => (
                  <tr key={m.id}>
                    <td className="nowrap muted">{fmtRelative(m.failed_at ?? m.created_at, tz)}</td>
                    <td className="mono">{m.provider_error_code ?? m.status}</td>
                    <td>{errorCodeText(m.provider_error_code ?? m.status)}</td>
                    <td className="nowrap">{m.to_number ? formatPhone(m.to_number) : ''}</td>
                    <td className="muted small" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.conversation_id ? <Link to={`/inbox/${m.conversation_id}`}>{m.body}</Link> : m.body}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Repeated texts (last 24 h)</h2>
        <p className="muted small">25 identical texts in a day are blocked. 15 shows a warning. Reminders rotate wordings so they stay under.</p>
        {h.repetition.length === 0 ? (
          <div className="muted">Nothing repeated enough to matter.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Count</th>
                  <th>Text</th>
                </tr>
              </thead>
              <tbody>
                {h.repetition.map((r) => (
                  <tr key={r.fingerprint}>
                    <td className={r.count_24h >= 25 ? 'danger-text' : r.count_24h >= 15 ? 'warn-text' : ''}>{r.count_24h}</td>
                    <td className="small">{r.sample_body}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {h.recent_webhook_events && (
        <div className="card">
          <h2>Recent activity from the phone</h2>
          {h.recent_webhook_events.length === 0 ? (
            <div className="muted">Nothing received yet. Run the two-way test to check replies can reach the app.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Outcome</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {h.recent_webhook_events.map((w) => (
                    <tr key={w.id}>
                      <td className="nowrap muted">{fmtRelative(w.received_at, tz)}</td>
                      <td>{eventWords(w.event_type)}</td>
                      <td>
                        <span className={`chip ${w.outcome === 'error' ? 'danger' : w.outcome === 'inbound_created' ? 'ok' : ''}`}>{outcomeWords(w.outcome)}</span>
                      </td>
                      <td className="small muted">{w.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AlarmCard({ a, lines, tz, onAck, acking }: { a: HealthEvent; lines: Line[]; tz: string; onAck: () => void; acking: boolean }) {
  const action = a.action ?? (a.what ? { what: a.what, doThis: a.do_this ?? [] } : ALARM_ACTIONS[a.type]) ?? { what: a.type, doThis: [] };
  const line = lines.find((l) => l.id === a.line_id);
  const code = typeof a.details?.code === 'string' ? (a.details.code as string) : null;
  return (
    <div className={`alarm ${a.severity === 'warn' ? 'warn' : ''}`}>
      <div className="row between wrap">
        <div>
          <strong>{action.what}</strong>
          {code && (
            <span className="mono" style={{ marginLeft: 8 }}>
              {code} — {errorCodeText(code)}
            </span>
          )}
          <div className="small muted">
            {line ? `${line.label ? `${line.label} · ` : ''}${formatPhone(line.phone_number)} · ` : ''}since {fmtFull(a.opened_at, tz)}
            {a.acknowledged_at && ` · acknowledged by ${a.acknowledged_by?.name ?? 'someone'} at ${fmtFull(a.acknowledged_at, tz)}`}
          </div>
          {a.type === 'stuck_sending' && <div className="tiny muted">Clears by itself 30 minutes after the last unknown send.</div>}
        </div>
        {!a.acknowledged_at && (
          <button className="btn small" onClick={onAck} disabled={acking} title="Seen, working on it: takes it off the red bar; it stays here until it clears">
            Acknowledge
          </button>
        )}
      </div>
      {action.doThis.length > 0 && (
        <ol>
          {action.doThis.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function LineCard({ line, tz, isAdmin, onResume, resuming }: { line: Line; tz: string; isAdmin: boolean; onResume: () => void; resuming: boolean }) {
  const h = line.health ?? null;
  const age = h?.heartbeat_age_s ?? ageSeconds(h?.last_heartbeat_at);
  const alarmAfter = h?.heartbeat_alarm_after_s ?? null;
  const ageTone = age == null ? 'danger' : alarmAfter != null ? (age > alarmAfter ? 'danger' : age > alarmAfter * 0.6 ? 'warn' : 'ok') : age > 900 ? 'warn' : 'ok';
  const { paused, forever } = linePaused(line);
  const isPhone = line.provider === 'httpsms';
  const sig = line.signature_configured ?? !!(line.provider_config && (line.provider_config.webhook_signing_key || line.provider_config.webhook_signing_key_env || line.provider_config.public_key || line.provider_config.webhook_secret));
  const eligible = h?.queue_eligible ?? null;
  const accepted = h?.queue_accepted ?? null;
  const copy = () => {
    if (!line.webhook_url) return;
    navigator.clipboard?.writeText(line.webhook_url).catch(() => {});
  };

  return (
    <div className="card">
      <div className="row between wrap" style={{ marginBottom: 12 }}>
        <div className="row wrap">
          <strong>{line.label ? `${line.label} · ` : ''}{formatPhone(line.phone_number)}</strong>
          <span className={`chip ${isPhone ? 'warn' : 'ok'}`}>{isPhone ? 'Phone line: can be cut off without notice' : 'Registered line'}</span>
          {!line.active && <span className="chip">Off</span>}
          {h && <span className={`chip ${h.status === 'ok' ? 'ok' : h.status === 'warn' ? 'warn' : h.status === 'alarm' ? 'danger' : ''}`}>{h.status === 'ok' ? 'all good' : h.status === 'warn' ? 'worth a look' : h.status === 'alarm' ? 'needs attention' : 'not checked yet'}</span>}
        </div>
        <span className="muted small">{h?.checked_at ? `checked ${fmtAgo(h.checked_at, tz)}` : 'not checked yet'}</span>
      </div>

      {paused && (
        <div className="notice danger" style={{ marginBottom: 12 }}>
          <div className="row between wrap">
            <span>
              <strong>PAUSED</strong> {line.paused_reason ? `— ${line.paused_reason}: ${errorCodeText(line.paused_reason)}` : ''}
              {forever ? ' · until an admin resumes' : ` · until ${fmtFull(line.paused_until, tz)}`}
            </span>
            {isAdmin && (
              <button className="btn small" onClick={() => confirm('Resume sending on this line? Only do this after the carrier problem is understood.') && onResume()} disabled={resuming}>
                Resume
              </button>
            )}
          </div>
        </div>
      )}

      {isPhone && (
        <div className="health-grid" style={{ marginBottom: 12 }}>
          <div className="stat">
            <div className="k">Phone last checked in</div>
            <div className={`v ${ageTone}`}>{age == null ? 'never' : `${fmtAgeSeconds(age)} ago`}</div>
            <div className="sub">normal: {fmtEvery(h?.heartbeat_median_gap_s)}{alarmAfter ? ` · alarm after ${fmtAgeSeconds(alarmAfter)}` : ''}</div>
          </div>
          <div className="stat">
            <div className="k">Sending speed</div>
            <div className={`v ${(h?.device_rate_per_minute ?? line.device_rate_per_minute ?? 0) < 6 ? 'warn' : ''}`}>{h?.device_rate_per_minute ?? line.device_rate_per_minute ?? '?'} <span style={{ fontSize: 14, fontWeight: 400 }}>a minute</span></div>
            <div className="sub">10 a minute is the right setting</div>
          </div>
          <div className="stat">
            <div className="k">Link to the phone</div>
            <div className={`v ${h?.fcm_token_present === false ? 'danger' : h?.fcm_token_present ? 'ok' : ''}`}>{h?.fcm_token_present == null ? 'unknown' : h.fcm_token_present ? 'working' : 'BROKEN'}</div>
            <div className="sub">{h?.fcm_token_present === false ? 'open the texting app on the phone and reopen it' : 'if this breaks, nothing can be sent'}</div>
          </div>
        </div>
      )}

      <div className="health-grid" style={{ marginBottom: 12 }}>
        <div className="stat">
          <div className="k">Queue</div>
          <div className={`v ${(h?.queue_depth_local ?? 0) > 10 ? 'danger' : (h?.queue_depth_local ?? 0) > 3 ? 'warn' : ''}`}>{h?.queue_depth_local ?? 0}</div>
          <div className="sub">
            {eligible != null || accepted != null ? `${eligible ?? '?'} ready · ${accepted ?? '?'} on the phone` : 'ready + on the phone'}
            {(h?.queue_depth_deferred ?? 0) > 0 && ` · ${pluralize(h!.queue_depth_deferred, 'text')} waiting for the window`}
          </div>
        </div>
        <div className="stat">
          <div className="k">Last reply received</div>
          <div className="v" style={{ fontSize: 16 }}>{fmtAgo(h?.last_inbound_webhook_at, tz)}</div>
          <div className="sub">{h?.last_outbound_sent_at ? `last text sent ${fmtAgo(h.last_outbound_sent_at, tz)}` : 'nothing sent yet'}</div>
        </div>
        <div className="stat">
          <div className="k">Last error</div>
          <div className="v" style={{ fontSize: 16 }}>{h?.last_error_code ? <span className="danger-text">{errorCodeText(h.last_error_code)}</span> : 'none'}</div>
          <div className="sub">{h?.last_error_at ? `${errorCodeText(h.last_error_code)} · ${fmtAgo(h.last_error_at, tz)}` : ''}</div>
        </div>
        <div className="stat">
          <div className="k">Two-way test</div>
          <div className="v" style={{ fontSize: 16 }}>{line.last_self_test_passed_at ? <span className="ok-text">passed</span> : 'not yet'}</div>
          <div className="sub">{line.last_self_test_passed_at ? fmtFull(line.last_self_test_passed_at, tz) : <Link to="/health/self-test">run one</Link>}</div>
        </div>
      </div>

      {/* Setup detail is for the one day someone connects the phone, so it stays folded away.
          Everything above this line is what the office reads every day. */}
      {line.webhook_url && (
        <details className="notice" style={{ marginBottom: 8 }}>
          <summary style={{ cursor: 'pointer' }}>
            Setup: where replies come back{' '}
            {h?.webhook_config_ok === false
              ? <span className="chip danger">needs fixing</span>
              : sig
                ? <span className="chip ok">set up</span>
                : <span className="chip warn">not secured</span>}
          </summary>
          <div style={{ marginTop: 8 }}>
            <p className="small" style={{ marginTop: 0 }}>
              This is the address the phone sends customers' replies to. It is set once, when the
              phone is connected. You do not need it day to day.
            </p>
            <div className="row between wrap">
              <code style={{ wordBreak: 'break-all' }}>{line.webhook_url}</code>
              <button className="btn small" onClick={copy}>Copy</button>
            </div>
            {isPhone && (
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Paste it into the texting phone's account under Webhooks, and tick only the
                "message received" event.
              </div>
            )}
            <div className="tiny muted" style={{ marginTop: 6 }}>
              {sig
                ? <span className="ok-text">Secured, so only your phone can post replies here.</span>
                : <span className="warn-text">Not secured yet: anyone who guessed this address could post fake customer texts. Add the secret when you connect the phone.</span>}
            </div>
            {h?.webhook_config_ok === false && (
              <div className="tiny danger-text" style={{ marginTop: 6 }}>
                The phone is currently sending replies somewhere else
                {h.webhook_url_registered ? ` (${h.webhook_url_registered})` : ''}, so replies will
                not reach this app. Paste the address above into the phone's account.
              </div>
            )}
          </div>
        </details>
      )}
      {h?.detail && <div className="muted small">{h.detail}</div>}
    </div>
  );
}
